<script>
  import { useForm } from '@inertiajs/svelte'
  import { liveValidate } from '../../lib/live_validation.svelte.js'
  import Layout from '../../layouts/layout.svelte'
  import PageHeader from '../../components/page_header.svelte'
  import FormLayout from '../../components/form_layout.svelte'
  import FormSection from '../../components/form_section.svelte'
  import ScheduleEditor from '../../components/schedule_editor.svelte'
  import TargetingEditor from '../../components/targeting_editor.svelte'
  import TextField from '../../components/form/text_field.svelte'
  import SelectField from '../../components/form/select_field.svelte'
  import NumberField from '../../components/form/number_field.svelte'
  import ToggleField from '../../components/form/toggle_field.svelte'
  import SubmitButton from '../../components/form/submit_button.svelte'
  import { Button } from '../../components/ui'

  let { boost_template, options = {}, back_url = '/boosts' } = $props()

  const isEdit = !!boost_template.id

  // Precognitive form: liveValidate asks the real create/update action to validate each
  // changed field (`precognition_for!` before any write), so the model is the only copy of
  // the rules. The controller nulls the multiplier fields the chosen type doesn't use and
  // turns the TargetingEditor's lists + the schedule rows into `_replace` collections.
  // `return_to` brings the save back to the filtered list the form was opened from; a
  // duplicate's `parent_id` lets a failed save re-render from the same parent.
  const form = useForm(isEdit ? 'patch' : 'post', isEdit ? `/boosts/${boost_template.id}` : '/boosts', {
    return_to: back_url,
    parent_id: boost_template.parent_id ?? null,
    boost_template: {
      name: boost_template.name ?? '',
      display_name: boost_template.display_name ?? '',
      area_id: boost_template.area_id ?? '',
      active: boost_template.active ?? true,
      super_boost: boost_template.super_boost ?? false,
      highlight: boost_template.highlight ?? '',
      members_only: boost_template.members_only ?? false,
      manual_multiplier: boost_template.manual_multiplier ?? true,
      multiplier: boost_template.multiplier ?? 2,
      multiplier_lower_bound: boost_template.multiplier_lower_bound ?? null,
      multiplier_upper_bound: boost_template.multiplier_upper_bound ?? null,
      schedule_attributes: boost_template.schedule_attributes,
      category_ids: boost_template.category_ids ?? [],
      sub_category_ids: boost_template.sub_category_ids ?? [],
      places: boost_template.places ?? [],
      excluded_sub_category_ids: boost_template.excluded_sub_category_ids ?? [],
      excluded_place_ids: boost_template.excluded_place_ids ?? []
    }
  })
  liveValidate(form, 'boost_template')

  const areaName = $derived((options.area_options ?? []).find((area) => area.value === $form.boost_template.area_id)?.label)

  const errorFor = (key) => $form.errors[`boost_template.${key}`]

  const submit = () => $form.submit({ preserveScroll: true })
</script>

<Layout>
  <PageHeader title={isEdit ? 'Edit Boost' : 'New Boost'} subtitle="Multiply points earned at targeted places on a schedule" />

  <FormLayout
    onsubmit={(event) => {
      event.preventDefault()
      submit()
    }}
  >
    <FormSection title="Boost Setup">
      <TextField bind:value={$form.boost_template.name} label="Name (Internal)" name="name" error={errorFor('name')} required />
      <SelectField bind:value={$form.boost_template.area_id} label="College Town" name="area_id" options={options.area_options ?? []} error={errorFor('area_id')} required />
      <TextField bind:value={$form.boost_template.display_name} label="Display Name (Optional)" name="display_name" error={errorFor('display_name')} />
    </FormSection>

    <FormSection title="Boost Configuration">
      <!-- Super Boost / Members Only are read-only exactly as in the old CMS BoostForm:
           shown, never editable, and submitted as stored. -->
      <ToggleField
        bind:checked={$form.boost_template.super_boost}
        label="Super Boost"
        name="super_boost"
        help="Super boosts are highlighted prominently in the app."
        error={errorFor('super_boost')}
        disabled
      />
      {#if $form.boost_template.super_boost}
        <TextField
          bind:value={$form.boost_template.highlight}
          label="Highlight Text"
          name="highlight"
          help="This text will be displayed on the super boost card."
          error={errorFor('highlight')}
          required
        />
      {/if}
      <ToggleField
        bind:checked={$form.boost_template.members_only}
        label="Members Only"
        name="members_only"
        help="Only show this boost to subscribed members."
        error={errorFor('members_only')}
        disabled
      />

      <div class="divider"></div>

      <div>
        <span class="label-text block mb-2">Multiplier Type</span>
        <div class="flex gap-6">
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              class="radio radio-sm"
              name="manual_multiplier"
              checked={$form.boost_template.manual_multiplier === true}
              onchange={() => ($form.boost_template.manual_multiplier = true)}
            />
            Fixed Multiplier
          </label>
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              class="radio radio-sm"
              name="manual_multiplier"
              checked={$form.boost_template.manual_multiplier === false}
              onchange={() => ($form.boost_template.manual_multiplier = false)}
            />
            Random Range
          </label>
        </div>
      </div>

      {#if $form.boost_template.manual_multiplier}
        <NumberField
          bind:value={$form.boost_template.multiplier}
          label="Multiplier Value"
          name="multiplier"
          step="0.1"
          help="Points will be multiplied by this value (e.g., 2 = double points)."
          error={errorFor('multiplier')}
          required
        />
      {:else}
        <div class="flex gap-4">
          <NumberField
            bind:value={$form.boost_template.multiplier_lower_bound}
            label="Lower Bound"
            name="multiplier_lower_bound"
            step="0.1"
            error={errorFor('multiplier_lower_bound')}
            required
          />
          <NumberField
            bind:value={$form.boost_template.multiplier_upper_bound}
            label="Upper Bound"
            name="multiplier_upper_bound"
            step="0.1"
            error={errorFor('multiplier_upper_bound')}
            required
          />
        </div>
        <p class="text-xs text-base-content/60">A random multiplier will be generated within this range each day.</p>
      {/if}
    </FormSection>

    <FormSection
      title="Target Places"
      description="Select which places should receive this boost. You can target by category, subcategory, or select specific places. Exclusions override inclusions — if a place is both included and excluded, it will NOT receive the boost."
    >
      <TargetingEditor
        bind:value={$form.boost_template}
        categories={options.categories ?? []}
        subCategories={options.sub_categories ?? []}
        placeSourceParams={{ area_id: $form.boost_template.area_id, checkin_enabled: true }}
        allowPlaceRadius={false}
        error={errorFor('excluded_place_ids')}
      />
    </FormSection>

    <FormSection title="Schedule">
      <ScheduleEditor bind:value={$form.boost_template.schedule_attributes} timeZones={options.time_zones ?? []} error={errorFor('schedule_attributes')} />
    </FormSection>

    <FormSection title={isEdit ? 'Save Your Changes' : 'Set Live'}>
      <ToggleField
        bind:checked={$form.boost_template.active}
        label="Active"
        name="active"
        help={isEdit
          ? 'When active, this boost template runs on the schedule above. Turn off to save as inactive.'
          : `When active, this boost template will be set live${areaName ? ` in ${areaName}` : ''}. Turn off to save as inactive.`}
        error={errorFor('active')}
      />
      {#if errorFor('base')}<p class="text-sm text-error">{errorFor('base')}</p>{/if}
    </FormSection>

    {#snippet footer()}
      <Button href={back_url} variant="ghost" size="sm">Cancel</Button>
      <SubmitButton processing={$form.processing} full={false} />
    {/snippet}
  </FormLayout>
</Layout>
